
import {
  AuthorType,
  LoadContactsResponse,
  LoadMessagesResponse,
  LoadMetaDataResponse,
  LoanTeamMember,
  MarkMessagesAsReadResponse,
  OriginationMessage,
  SendMessageResponse,
} from '@ld.consumer-portal/models';
import {
  CUSTOMER_CARE_DESCRIPTION,
  CUSTOMER_CARE_EMAIL_RETAIL,
  CUSTOMER_CARE_HOURS_RETAIL,
  CUSTOMER_CARE_PHONE_DISPLAY,
  CUSTOMER_CARE_PHONE_TEL,
  MORTGAGE_PAYMENTS_DESCRIPTION,
  MORTGAGE_PAYMENTS_EMAIL,
  MORTGAGE_PAYMENTS_HOURS,
  MORTGAGE_PAYMENTS_LINK_LABEL,
  MORTGAGE_PAYMENTS_PHONE_DISPLAY,
  MORTGAGE_PAYMENTS_PHONE_TEL,
  MORTGAGE_PAYMENTS_URL,
  MORTGAGE_PROCESS_BROCHURE_URL,
  PERSONAL_LOAN_PAYMENTS_DESCRIPTION,
  PERSONAL_LOAN_PAYMENTS_HOURS,
  PERSONAL_LOAN_PAYMENTS_LINK_LABEL,
  PERSONAL_LOAN_PAYMENTS_PHONE_DISPLAY,
  PERSONAL_LOAN_PAYMENTS_PHONE_TEL,
  PERSONAL_LOAN_PAYMENTS_URL,
} from '@ld.consumer-portal/constants';
import { EndpointScenarioMap } from './mock.types';

/**
 * Mock contacts scenarios include optional `primaryContact` and `members` fields.
 * These are used by `mapLoanContactsResponse()` in origination.service.ts to bypass
 * the API-format mapping in `mapLoanContact()`, which expects fields like `agentId`,
 * `firstName`, `lastName` rather than the display-format `id`, `name` used here.
 */
type MockContactsResponse = LoadContactsResponse & {
  primaryContact?: LoanTeamMember;
  members?: LoanTeamMember[];
};

export const originationContactsScenarios: EndpointScenarioMap<MockContactsResponse> = {
  default: {
    primaryContact: {
      id: 'lo-001',
      name: 'POS Banker',
      role: 'loan-officer',
      roleDisplayName: 'Mortgage Banker',
      phone: '(925) 444-4444',
      email: 'posbanker@loandepot.com',
      nmlsId: '999',
    },
    members: [
      {
        id: 'pa-001',
        name: 'Ernie Correa',
        role: 'production-assistant',
        roleDisplayName: 'Production Assistant',
        phone: '(949) 404-7433',
        email: 'ECorrea@loandepot.com',
      },
    ],
    contacts: [
      {
        id: 'lo-001',
        name: 'POS Banker',
        title: 'Mortgage Banker',
        role: 'mortgage-banker',
        nmlsId: '999',
        phone: '(925) 444-4444',
        email: 'posbanker@loandepot.com',
        phones: [
          { label: 'Direct', number: '(925) 444-4444', link: 'tel:9254444444' },
          { label: 'Toll Free', number: '(888) 337-6888', link: 'tel:8883376888' },
        ],
      },
      {
        id: 'pa-001',
        name: 'Ernie Correa',
        title: 'Production Assistant',
        role: 'production-assistant',
        phone: '(949) 404-7433',
        email: 'ECorrea@loandepot.com',
        phones: [
          { label: 'Direct', number: '(949) 404-7433', link: 'tel:9494047433' },
          { label: 'Toll Free', number: '(888) 337-6888 x4047433', link: 'tel:8883376888,4047433' },
        ],
      },
    ],
    support: [
      {
        name: 'Customer Care',
        description: CUSTOMER_CARE_DESCRIPTION,
        phones: [{ label: 'Phone', number: CUSTOMER_CARE_PHONE_DISPLAY, link: CUSTOMER_CARE_PHONE_TEL }],
        email: CUSTOMER_CARE_EMAIL_RETAIL,
        schedule: [{ label: '', hours: CUSTOMER_CARE_HOURS_RETAIL }],
        brochureUrl: MORTGAGE_PROCESS_BROCHURE_URL,
      },
      {
        name: 'Mortgage Loan Payments',
        description: MORTGAGE_PAYMENTS_DESCRIPTION,
        phones: [{ label: 'Phone', number: MORTGAGE_PAYMENTS_PHONE_DISPLAY, link: MORTGAGE_PAYMENTS_PHONE_TEL }],
        email: MORTGAGE_PAYMENTS_EMAIL,
        schedule: [{ label: '', hours: MORTGAGE_PAYMENTS_HOURS }],
        websiteUrl: MORTGAGE_PAYMENTS_URL,
        websiteLabel: MORTGAGE_PAYMENTS_LINK_LABEL,
      },
      {
        name: 'Personal Loan Payments',
        description: PERSONAL_LOAN_PAYMENTS_DESCRIPTION,
        phones: [{ label: 'Phone', number: PERSONAL_LOAN_PAYMENTS_PHONE_DISPLAY, link: PERSONAL_LOAN_PAYMENTS_PHONE_TEL }],
        schedule: [{ label: '', hours: PERSONAL_LOAN_PAYMENTS_HOURS }],
        websiteUrl: PERSONAL_LOAN_PAYMENTS_URL,
        websiteLabel: PERSONAL_LOAN_PAYMENTS_LINK_LABEL,
      },
    ],
  },

  'full-team': {
    primaryContact: {
      id: 'lo-002',
      name: 'Emily Davis',
      role: 'loan-officer',
      roleDisplayName: 'Senior Loan Officer',
      phone: '(555) 345-6789',
      email: 'emily.davis@loandepot.com',
      nmlsId: '234567',
      address: '456 Oak Avenue, Costa Mesa, CA 92627',
    },
    members: [
      {
        id: 'pa-002',
        name: 'Michael Chen',
        role: 'production-assistant',
        roleDisplayName: 'Production Assistant',
        phone: '(555) 456-7890',
        email: 'michael.chen@loandepot.com',
      },
      {
        id: 'sam-001',
        name: 'Lisa Thompson',
        role: 'senior-account-manager',
        roleDisplayName: 'Senior Account Manager',
        phone: '(555) 567-8901',
        email: 'lisa.thompson@loandepot.com',
      },
      {
        id: 'pm-001',
        name: 'James Wilson',
        role: 'processing-manager',
        roleDisplayName: 'Processing Manager',
        phone: '(555) 678-9012',
        email: 'james.wilson@loandepot.com',
      },
    ],
    contacts: [
      {
        id: 'lo-002',
        name: 'Emily Davis',
        title: 'Senior Loan Officer',
        role: 'loan-officer',
        phone: '555-345-6789',
        email: 'emily.davis@loandepot.com',
        nmlsId: '234567',
        phones: [
          { label: 'Direct', number: '(555) 345-6789', link: 'tel:5553456789' },
          { label: 'Toll Free', number: '(888) 337-6888', link: 'tel:8883376888' },
        ],
      },
      {
        id: 'pa-002',
        name: 'Michael Chen',
        title: 'Production Assistant',
        role: 'production-assistant',
        phone: '555-456-7890',
        email: 'michael.chen@loandepot.com',
        phones: [
          { label: 'Direct', number: '(555) 456-7890', link: 'tel:5554567890' },
        ],
      },
      {
        id: 'sam-001',
        name: 'Lisa Thompson',
        title: 'Senior Account Manager',
        role: 'senior-account-manager',
        phone: '555-567-8901',
        email: 'lisa.thompson@loandepot.com',
        phones: [
          { label: 'Direct', number: '(555) 567-8901', link: 'tel:5555678901' },
        ],
      },
      {
        id: 'pm-001',
        name: 'James Wilson',
        title: 'Processing Manager',
        role: 'processing-manager',
        phone: '555-678-9012',
        email: 'james.wilson@loandepot.com',
        phones: [
          { label: 'Direct', number: '(555) 678-9012', link: 'tel:5556789012' },
        ],
      },
    ],
    support: [
      {
        name: 'Customer Care',
        description: CUSTOMER_CARE_DESCRIPTION,
        phones: [{ label: 'Phone', number: CUSTOMER_CARE_PHONE_DISPLAY, link: CUSTOMER_CARE_PHONE_TEL }],
        email: CUSTOMER_CARE_EMAIL_RETAIL,
        schedule: [{ label: '', hours: CUSTOMER_CARE_HOURS_RETAIL }],
        brochureUrl: MORTGAGE_PROCESS_BROCHURE_URL,
      },
      {
        name: 'Mortgage Loan Payments',
        description: MORTGAGE_PAYMENTS_DESCRIPTION,
        phones: [{ label: 'Phone', number: MORTGAGE_PAYMENTS_PHONE_DISPLAY, link: MORTGAGE_PAYMENTS_PHONE_TEL }],
        email: MORTGAGE_PAYMENTS_EMAIL,
        schedule: [{ label: '', hours: MORTGAGE_PAYMENTS_HOURS }],
        websiteUrl: MORTGAGE_PAYMENTS_URL,
        websiteLabel: MORTGAGE_PAYMENTS_LINK_LABEL,
      },
      {
        name: 'Personal Loan Payments',
        description: PERSONAL_LOAN_PAYMENTS_DESCRIPTION,
        phones: [{ label: 'Phone', number: PERSONAL_LOAN_PAYMENTS_PHONE_DISPLAY, link: PERSONAL_LOAN_PAYMENTS_PHONE_TEL }],
        schedule: [{ label: '', hours: PERSONAL_LOAN_PAYMENTS_HOURS }],
        websiteUrl: PERSONAL_LOAN_PAYMENTS_URL,
        websiteLabel: PERSONAL_LOAN_PAYMENTS_LINK_LABEL,
      },
    ],
  },

  'loan-officer-only': {
    contacts: [
      {
        id: 'lo-003',
        name: 'Robert Williams',
        title: 'Loan Officer',
        role: 'loan-officer',
        phone: '555-789-0123',
        email: 'robert.williams@loandepot.com',
        nmlsId: '345678',
        phones: [
          { label: 'Direct', number: '(555) 789-0123', link: 'tel:5557890123' },
          { label: 'Toll Free', number: '(888) 337-6888', link: 'tel:8883376888' },
        ],
      },
    ],
    support: [
      {
        name: 'Customer Care',
        description: CUSTOMER_CARE_DESCRIPTION,
        phones: [{ label: 'Phone', number: CUSTOMER_CARE_PHONE_DISPLAY, link: CUSTOMER_CARE_PHONE_TEL }],
        email: CUSTOMER_CARE_EMAIL_RETAIL,
        schedule: [{ label: '', hours: CUSTOMER_CARE_HOURS_RETAIL }],
        brochureUrl: MORTGAGE_PROCESS_BROCHURE_URL,
      },
      {
        name: 'Mortgage Loan Payments',
        description: MORTGAGE_PAYMENTS_DESCRIPTION,
        phones: [{ label: 'Phone', number: MORTGAGE_PAYMENTS_PHONE_DISPLAY, link: MORTGAGE_PAYMENTS_PHONE_TEL }],
        email: MORTGAGE_PAYMENTS_EMAIL,
        schedule: [{ label: '', hours: MORTGAGE_PAYMENTS_HOURS }],
        websiteUrl: MORTGAGE_PAYMENTS_URL,
        websiteLabel: MORTGAGE_PAYMENTS_LINK_LABEL,
      },
      {
        name: 'Personal Loan Payments',
        description: PERSONAL_LOAN_PAYMENTS_DESCRIPTION,
        phones: [{ label: 'Phone', number: PERSONAL_LOAN_PAYMENTS_PHONE_DISPLAY, link: PERSONAL_LOAN_PAYMENTS_PHONE_TEL }],
        schedule: [{ label: '', hours: PERSONAL_LOAN_PAYMENTS_HOURS }],
        websiteUrl: PERSONAL_LOAN_PAYMENTS_URL,
        websiteLabel: PERSONAL_LOAN_PAYMENTS_LINK_LABEL,
      },
    ],
  },
};

export const originationMessagesScenarios: EndpointScenarioMap<LoadMessagesResponse> = {
  default: {
    messages: [
      {
        messageId: 'msg-001',
        authorName: 'John Pearson',
        authorTitle: 'Loan Officer',
        authorType: AuthorType.AGENT,
        isMe: false,
        messageText:
          'Hi! Welcome to your loan application. I am your loan officer John. Please let me know if you have any questions!',
        sentOn: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        unread: false,
      },
    ],
    fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  },

  'with-conversation': {
    messages: [
      {
        messageId: 'msg-001',
        authorName: 'John Pearson',
        authorTitle: 'Loan Officer',
        authorType: AuthorType.AGENT,
        isMe: false,
        messageText: 'Hi! Welcome to your loan application. I am your loan officer John.',
        sentOn: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        unread: false,
      },
      {
        messageId: 'msg-002',
        authorName: 'You',
        authorType: AuthorType.BORROWER,
        isMe: true,
        messageText: 'Thank you John! When should I expect to hear about my application status?',
        sentOn: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        unread: false,
      },
      {
        messageId: 'msg-003',
        authorName: 'John Pearson',
        authorTitle: 'Loan Officer',
        authorType: AuthorType.AGENT,
        isMe: false,
        messageText:
          'Great question! We have received your application and are currently reviewing your documents. You should hear back within 2-3 business days.',
        sentOn: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        unread: false,
      },
      {
        messageId: 'msg-004',
        authorName: 'Sarah Johnson',
        authorTitle: 'Processor',
        authorType: AuthorType.AGENT,
        isMe: false,
        messageText:
          'Hi, this is Sarah from processing. We need an additional bank statement for the months of January. Can you please upload that at your earliest convenience?',
        sentOn: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        unread: true,
      },
    ],
    fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  },

  'no-messages': {
    messages: [],
    fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  },

  'unread-messages': {
    messages: [
      {
        messageId: 'msg-001',
        authorName: 'John Pearson',
        authorTitle: 'Loan Officer',
        authorType: AuthorType.AGENT,
        isMe: false,
        messageText: 'Great news! Your appraisal has been completed and the value came in at $545,000.',
        sentOn: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        unread: true,
      },
      {
        messageId: 'msg-002',
        authorName: 'John Pearson',
        authorTitle: 'Loan Officer',
        authorType: AuthorType.AGENT,
        isMe: false,
        messageText: 'We are now moving forward with underwriting. I will keep you updated on the progress.',
        sentOn: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        unread: true,
      },
    ],
    fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  },
};

export const sendMessageScenarios: EndpointScenarioMap<SendMessageResponse> = {
  default: {
    message: {
      messageId: 'msg-new-001',
      authorName: 'You',
      authorType: AuthorType.BORROWER,
      isMe: true,
      messageText: 'Message sent successfully',
      sentOn: new Date().toISOString(),
      unread: false,
    },
    trackId: 'track-001',
  },
  failed: {
    message: {
      messageId: '',
      authorName: '',
      authorType: AuthorType.BORROWER,
      isMe: true,
      messageText: '',
      sentOn: '',
      unread: false,
    },
    trackId: 'track-002',
  },
};

export const markMessagesReadScenarios: EndpointScenarioMap<MarkMessagesAsReadResponse> = {
  default: {
    messageIds: ['msg-001', 'msg-002'],
  },
  failed: {
    messageIds: [],
  },
};

const initialMessage: OriginationMessage = {
  messageId: 'msg-stateful-001',
  authorName: 'John Pearson',
  authorTitle: 'Loan Officer',
  authorType: AuthorType.AGENT,
  isMe: false,
  messageText: 'Hi! Your appraisal has been completed and the value came in at $545,000.',
  sentOn: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  unread: true,
};

const secondMessage: OriginationMessage = {
  messageId: 'msg-stateful-002',
  authorName: 'John Pearson',
  authorTitle: 'Loan Officer',
  authorType: AuthorType.AGENT,
  isMe: false,
  messageText: 'We are now moving forward with underwriting. I will keep you updated on the progress.',
  sentOn: new Date(Date.now() - 30 * 1000).toISOString(),
  unread: true,
};

class OriginationMessageMockState {
  private messages: OriginationMessage[] = [{ ...initialMessage }];
  private loadCallCount = 0;
  private readMessageIds = new Set<string>();

  getMessages(): LoadMessagesResponse {
    this.loadCallCount++;

    if (this.loadCallCount > 1 && this.readMessageIds.has(initialMessage.messageId)) {
      const hasSecond = this.messages.some((m) => m.messageId === secondMessage.messageId);
      if (!hasSecond) {
        this.messages.push({ ...secondMessage });
      }
    }

    const result = this.messages.map((m) => ({
      ...m,
      unread: this.readMessageIds.has(m.messageId) ? false : m.unread,
    }));

    return {
      messages: result,
      fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    };
  }

  markAsRead(messageIds: string[]): void {
    for (const id of messageIds) {
      this.readMessageIds.add(id);
    }
  }

  getUnreadCount(): number {
    return this.messages.filter((m) => m.unread && !this.readMessageIds.has(m.messageId)).length;
  }

  reset(): void {
    this.messages = [{ ...initialMessage }];
    this.loadCallCount = 0;
    this.readMessageIds.clear();
  }
}

export const originationMessageMockState = new OriginationMessageMockState();

export const originationMetadataScenarios: EndpointScenarioMap<LoadMetaDataResponse> = {
  default: {
    unreadMessageCount: 0,
    showMessages: true,
    showContacts: true,
    showToDos: true,
  },
  'with-unread': {
    unreadMessageCount: 1,
    showMessages: true,
    showContacts: true,
    showToDos: true,
  },
};
